'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { authClient } from '@bip/api-client';
import { PROXY_PREFIX } from '../config';

/**
 * Shared left account-nav sidebar (CMS My Profile.html:30-37 / design ProfileSidebar.jsx).
 * Repeats the same four items the navbar avatar dropdown shows. Rendered on every account page
 * (My Profile, Change Password, Manage Bookings). Links use the CLEAN top-level URLs; the
 * next.config.js rewrites map them onto the real `/hotels/*` routes on the hotels deploy.
 * `active` matches via `endsWith(route)` so it highlights whether the browser shows the clean URL
 * (after a rewrite) or the underlying `/hotels/*` path.
 */
const NAV_ITEMS = [
  { route: '/my-profile', icon: 'fa-regular fa-user', label: 'Personal details' },
  { route: '/manage-bookings', icon: 'fa-regular fa-rectangle-list', label: 'My Bookings' },
  { route: '/change-password', icon: 'fa-regular fa-lock', label: 'Change Password' },
];

/** Item classes shared by the 3 nav links + the Sign Out button — exact port of
 *  `ul.profile-list li a` (globals.css:2509): 18px/500-weight black text, 5px radius,
 *  0.75rem/1rem padding, 1px bottom gap between rows. Active/hover ONLY swap the background to
 *  `--primary-hover` (#dc9cf7) — the text STAYS black, never `text-primary`. */
const ITEM_CLASS =
  'flex items-center gap-2 rounded-[0.313rem] px-3 py-2.5 text-[1.125rem] font-medium text-black mb-[1px] transition-colors max-lg:px-2! max-lg:py-2 max-lg:text-[0.938rem] max-lg:mb-0';

export function AccountSidebar() {
  const pathname = usePathname();

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    await authClient.logout();
    window.location.href = `${PROXY_PREFIX}/hotels`;
  };

  return (
    <aside className="bg-white rounded-lg px-4 py-6 mb-10 max-lg:px-2! max-lg:py-2 max-lg:mb-4">
      <ul className="m-0 p-0 max-lg:flex max-lg:gap-1 max-lg:overflow-x-auto">
        {NAV_ITEMS.map(({ route, icon, label }) => {
          const href = `${PROXY_PREFIX}${route}`;
          const active = pathname === href || pathname.endsWith(route);
          return (
            <li key={route} className="list-none max-lg:shrink-0">
              <Link
                href={href}
                className={`${ITEM_CLASS} ${active ? 'bg-[#dc9cf7]' : 'hover:bg-[#dc9cf7]'}`}
              >
                <i className={`${icon} w-5 text-center`} />
                <span className="max-lg:whitespace-nowrap">{label}</span>
              </Link>
            </li>
          );
        })}
        <li className="list-none max-lg:shrink-0">
          {/* `!` (important) forced on font-size + hover-background — `styles/base.css:46-59` has
              a global `button { font-size: inherit; background: transparent; }` reset that only
              targets `<button>` (not the `<a>`/Link the other 3 items use), which is why Sign Out
              alone looked like a different size and had no hover feedback. */}
          <button
            type="button"
            onClick={handleLogout}
            className={`w-full ${ITEM_CLASS} text-[1.125rem]! hover:bg-[#dc9cf7]! max-lg:w-auto! max-lg:text-[0.938rem]!`}
          >
            <i className="fa-regular fa-right-from-bracket w-5 text-center" />
            <span className="max-lg:whitespace-nowrap">Sign Out</span>
          </button>
        </li>
      </ul>
    </aside>
  );
}
